// src/components/provider/SpotListing.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { addSpotListing, updateSpotListing, deleteSpotListing, getProviderSpots } from '../../services/firestore';
import Calendar from '../common/Calendar';

const SpotListing = () => {
  const { currentUser, userProfile } = useAuth();
  
  // State for spot listings
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for new spot form
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    ageCategory: '',
    type: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    notes: '',
    recurring: false,
    daysOfWeek: []
  });
  
  // State for editing
  const [editingSpotId, setEditingSpotId] = useState(null);
  
  // Fetch provider's spots
  useEffect(() => {
    const fetchSpots = async () => {
      try {
        if (currentUser) {
          const spotsList = await getProviderSpots(currentUser.uid);
          setSpots(spotsList);
        }
      } catch (err) {
        setError('Error fetching spots: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSpots();
  }, [currentUser]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle day of week selection for recurring spots
  const handleDaySelect = (day) => {
    setFormData(prev => {
      if (prev.daysOfWeek.includes(day)) {
        return {
          ...prev,
          daysOfWeek: prev.daysOfWeek.filter(d => d !== day)
        };
      } else {
        return {
          ...prev,
          daysOfWeek: [...prev.daysOfWeek, day]
        };
      }
    });
  };
  
  // Reset form to default values
  const resetForm = () => {
    setFormData({
      ageCategory: '',
      type: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      notes: '',
      recurring: false,
      daysOfWeek: []
    });
    setEditingSpotId(null);
  };
  
  // Open form for editing a spot
  const handleEditSpot = (spot) => {
    setFormData({
      ageCategory: spot.ageCategory,
      type: spot.type,
      startDate: spot.startDate,
      endDate: spot.endDate,
      startTime: spot.startTime,
      endTime: spot.endTime,
      notes: spot.notes || '',
      recurring: spot.recurring || false,
      daysOfWeek: spot.daysOfWeek || []
    });
    setEditingSpotId(spot.id);
    setShowForm(true);
    
    // Scroll to form
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.ageCategory || !formData.type || !formData.startDate) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const spotData = {
        ...formData,
        providerName: userProfile?.displayName || 'Provider'
      };
      
      if (editingSpotId) {
        // Update existing spot
        await updateSpotListing(editingSpotId, spotData);
        
        // Update local state
        setSpots(prev => prev.map(spot => 
          spot.id === editingSpotId ? { ...spot, ...spotData } : spot
        ));
      } else {
        // Create new spot
        const newSpot = await addSpotListing(currentUser.uid, spotData);
        
        // Update local state
        setSpots(prev => [newSpot, ...prev]);
      }
      
      // Reset form
      resetForm();
      setShowForm(false);
    } catch (err) {
      setError('Error saving spot: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle spot deletion
  const handleDeleteSpot = async (spotId) => {
    if (window.confirm('Are you sure you want to delete this spot?')) {
      setLoading(true);
      
      try {
        await deleteSpotListing(spotId);
        
        // Update local state
        setSpots(prev => prev.filter(spot => spot.id !== spotId));
      } catch (err) {
        setError('Error deleting spot: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  // Get days of week as string
  const getDaysOfWeekString = (daysArray) => {
    if (!daysArray || daysArray.length === 0) return 'N/A';
    
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return daysArray.map(day => days[day]).join(', ');
  };
  
  return (
    <div className="spot-listing">
      <h1>Manage Available Spots</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="action-buttons">
        <button 
          className="primary-button"
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancel' : 'Add New Spot'}
        </button>
      </div>
      
      {/* Add/Edit Spot Form */}
      {showForm && (
        <div className="spot-form-container">
          <h2>{editingSpotId ? 'Edit Spot' : 'Add New Spot'}</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ageCategory">Age Category *</label>
                <select
                  id="ageCategory"
                  name="ageCategory"
                  value={formData.ageCategory}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Age Category</option>
                  <option value="Infant/Toddler">Infant/Toddler (6 weeks–29 months)</option>
                  <option value="Preschool">Preschool (30 months–Kindergarten entry)</option>
                  <option value="School-Age">School-Age (Grade 1–13 years)</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="type">Spot Type *</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Spot Type</option>
                  <option value="full-time">Full-Time</option>
                  <option value="part-time">Part-Time</option>
                  <option value="drop-in">Drop-In</option>
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={formData.startDate}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startTime">Start Time</label>
                <input
                  type="time"
                  id="startTime"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="endTime">End Time</label>
                <input
                  type="time"
                  id="endTime"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="recurring"
                  checked={formData.recurring}
                  onChange={handleChange}
                />
                Recurring Spot
              </label>
            </div>
            
            {formData.recurring && (
              <div className="form-group">
                <label>Days of Week:</label>
                <div className="days-of-week">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                    <label key={day} className="day-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.daysOfWeek.includes(index)}
                        onChange={() => handleDaySelect(index)}
                      />
                      {day}
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add any additional information about this spot"
                rows={4}
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'Saving...' : (editingSpotId ? 'Update Spot' : 'Add Spot')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Calendar View (Placeholder) */}
      <div className="calendar-view">
        <h2>Calendar View</h2>
        <p className="placeholder-text">Calendar view will be implemented here to show all spots visually.</p>
      </div>
      
      {/* Spots List */}
      <div className="spots-list">
        <h2>Your Listed Spots</h2>
        
        {loading && <p>Loading spots...</p>}
        
        {!loading && spots.length === 0 && (
          <p className="no-spots">You haven't listed any spots yet.</p>
        )}
        
        {spots.map(spot => (
          <div className="spot-card" key={spot.id}>
            <div className="spot-header">
              <h3>{spot.ageCategory}</h3>
              <span className={`spot-type ${spot.type}`}>
                {spot.type.charAt(0).toUpperCase() + spot.type.slice(1)}
              </span>
            </div>
            
            <div className="spot-details">
              <p>
                <strong>Date: </strong>
                {formatDate(spot.startDate)}
                {spot.endDate && ` to ${formatDate(spot.endDate)}`}
              </p>
              
              {spot.startTime && spot.endTime && (
                <p>
                  <strong>Time: </strong>
                  {spot.startTime} to {spot.endTime}
                </p>
              )}
              
              {spot.recurring && (
                <p>
                  <strong>Recurring: </strong>
                  {getDaysOfWeekString(spot.daysOfWeek)}
                </p>
              )}
              
              {spot.notes && (
                <p>
                  <strong>Notes: </strong>
                  {spot.notes}
                </p>
              )}
            </div>
            
            <div className="spot-actions">
              <button 
                className="edit-button"
                onClick={() => handleEditSpot(spot)}
              >
                Edit
              </button>
              <button 
                className="delete-button"
                onClick={() => handleDeleteSpot(spot.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpotListing;